import {
  BlockStack,
  Button,
  Image,
  InlineStack,
  LegacyStack,
  Link,
  List,
  Text,
  useBreakpoints,
} from '@shopify/polaris'
import useSWR from 'swr'

import { backendFetchResultData } from '../common/api'
import { ExternalLink, useSystemData } from '../common/helpers'
import { LegacyHelpIcon } from '../common/HelpIcon'
import { customerPortalSettingsUrl, plansUrl, settingsUrl } from '../common/urls'
import { FreeSetupBanner } from './GettingStarted'

interface FirstSubscriptionProductData {
  url: string | null
}

const fetchFirstSubscriptionProductData = () =>
  backendFetchResultData<FirstSubscriptionProductData>(
    'GET',
    '/plans/first-subscription-product-data'
  )

const useFirstSubscriptionProductData = () =>
  useSWR('/plans/first-subscription-product-data', fetchFirstSubscriptionProductData)

export const FinalSteps = ({ onComplete }: { onComplete: () => void }) => {
  const { mdUp } = useBreakpoints()
  const systemData = useSystemData()

  const { data: firstSubscriptionProductData } = useFirstSubscriptionProductData()

  return (
    <BlockStack gap="400">
      <Text variant="headingMd" as="h2" fontWeight="bold">
        Quick Start - Optional steps
      </Text>
      <BlockStack gap="600">
        <FreeSetupBanner />
        <InlineStack gap="800" wrap={false} blockAlign="center">
          <LegacyStack.Item fill>
            <LegacyStack vertical spacing="loose">
              <Text as="p">
                Congratulations, you're now ready to sell! &nbsp;
                <span style={{ fontSize: '1.6em', verticalAlign: 'middle' }}>🎉</span>&nbsp; Here is
                a list of recommended further actions:
              </Text>
              <List gap="loose">
                <List.Item>
                  {firstSubscriptionProductData?.url ? (
                    <ExternalLink
                      href={`https://${systemData?.domain}${firstSubscriptionProductData.url}`}
                    >
                      Go to your product page
                    </ExternalLink>
                  ) : (
                    'Go to your product page'
                  )}{' '}
                  and try creating your first subscription
                  <LegacyHelpIcon>
                    Make sure to go to the theme where you installed the subscription widget
                  </LegacyHelpIcon>
                </List.Item>
                <List.Item>
                  <Link url={plansUrl}>Customize and add more products</Link> to your subscription
                  plans, to offer greater flexibility and boost sales
                </List.Item>
                <List.Item>
                  <Link url={customerPortalSettingsUrl()}>
                    Install and configure the Customer Portal
                  </Link>{' '}
                  to give your customers control over their subscriptions
                </List.Item>
                <List.Item>
                  <Link url={settingsUrl()}>Set up and customize email notifications</Link> to keep
                  your subscribers informed
                </List.Item>
                <List.Item>
                  <ExternalLink href="https://www.youtube.com/playlist?list=PLOHvuO73bXtX6SDP_o9Yzk0IEAefkQWbZ">
                    Watch our video tutorials
                  </ExternalLink>{' '}
                  and{' '}
                  <ExternalLink href="https://docs.paywhirl.com/en/collections/2664862-shopify-subscription-app">
                    browse the docs
                  </ExternalLink>{' '}
                  to learn more about PayWhirl
                </List.Item>
              </List>
              <Text as="p">
                You can always restart this wizard by clicking <strong>Quick Start</strong> in the
                Dashboard page menu.
              </Text>
            </LegacyStack>
          </LegacyStack.Item>
          {mdUp && (
            <LegacyStack.Item>
              <Image
                style={{ maxWidth: '150px', margin: '0 50px' }}
                source="/images/partying.svg"
                alt="Start Selling"
              />
            </LegacyStack.Item>
          )}
        </InlineStack>
        <InlineStack>
          <Button onClick={onComplete} variant="primary">
            Close
          </Button>
        </InlineStack>
      </BlockStack>
    </BlockStack>
  )
}
